import React from "react";
import { useMediaQuery } from "react-responsive";

// Components
import CountdownTimer from "components/layout/CountdownTimer";
import SignatureInput from "components/input/SignatureInput";

// Images & Icons
import BlackWarningIcon from "assets/icons/general/BlackWarningIcon.png";
import Spinner from "assets/icons/general/Spinner";

function QuestionnaireForm({
  assessmentQuestionnaire,
  values,
  errors,
  touched,
  setFieldValue,
  handleSubmit,
  formSection,
  totalOptionA,
  totalOptionB,
  totalOptionC,
  totalOptionD,
  setSelfAssesmentStart,
  isSelfAssessmentStart,
  setOpenTimerIsUp,
  isSubmitting,
  formIsSubmit,
  signatureRef,
  handleClear,
  handleSave,
}) {
  const smallScreen = useMediaQuery({ query: "(max-width: 899px)" });

  return (
    <div className="bg-gray-100 rounded-lg mx-4 px-6 py-4 md:px-10 md:py-8 space-y-6">
      <div className="flex flex-row justify-between items-center">
        <div>
          <p className="text-sky-500 uppercase text-lg md:text-xl xl:text-2xl font-bold">
            {assessmentQuestionnaire.name}
          </p>
        </div>
        {isSelfAssessmentStart && (
          <CountdownTimer
            timerDuration="30-minutes"
            setSelfAssesmentStart={setSelfAssesmentStart}
            isSelfAssessmentStart={isSelfAssessmentStart}
            setOpenTimerIsUp={setOpenTimerIsUp}
          />
        )}
      </div>
      {isSelfAssessmentStart && (
        <div className="flex flex-row gap-2 items-center p-4 text-sm sm:text-base xl:text-lg border-2 rounded-lg border-orange-500 bg-orange-100">
          <img
            className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6"
            src={BlackWarningIcon}
            alt="question-mark-icon"
          />
          <p>Please complete this form within 30 minutes</p>
        </div>
      )}
      {assessmentQuestionnaire &&
        assessmentQuestionnaire.img_urls &&
        assessmentQuestionnaire.img_urls.length > 0 && (
          <div className="flex items-center justify-center bg-white rounded-md border border-2 border-gray-400 p-4">
            {assessmentQuestionnaire.img_urls.map((url, y) => {
              return (
                <img
                  className="rounded-lg"
                  src={url}
                  alt="form-supporting-image"
                  key={y}
                />
              );
            })}
          </div>
        )}
      <table className="table-auto border-collapse border-4 border-black">
        <thead>
          <tr className="border-b-4 border-black">
            <th
              className={`${
                !formIsSubmit && "sticky top-40"
              } bg-gray-300 p-4 text-left border-x-4 border-black`}
              hidden={smallScreen}
            >
              <p className="uppercase text-base xl:text-lg font-bold">
                Subject Area
              </p>
              <p className="text-sm sm:text-base xl:text-lg font-normal">
                Description
              </p>
            </th>
            <th
              className={`${
                !formIsSubmit && "sticky top-40"
              } bg-gray-300 p-2 border-x-4 border-black`}
            >
              <p className="text-center text-[10px] text-sm sm:text-base font-semibold">
                I have never done or participated in this or I do not know{" "}
                <br /> <b>(A)</b>
              </p>
            </th>
            <th
              className={`${
                !formIsSubmit && "sticky top-40"
              } bg-gray-300 p-2 border-x-4 border-black`}
            >
              <p className="text-center text-[10px] text-sm sm:text-base font-semibold">
                I have done, or do this, under supervision <br /> <b>(B)</b>
              </p>
            </th>
            <th
              className={`${
                !formIsSubmit && "sticky top-40"
              } bg-gray-300 p-2 border-x-4 border-black`}
            >
              <p className="text-center text-[10px] text-sm sm:text-base font-semibold">
                I sometimes do this, or have done this during the past 3 years{" "}
                <br /> <b>(C)</b>
              </p>
            </th>
            <th
              className={`${
                !formIsSubmit && "sticky top-40"
              } bg-gray-300 p-2 border-x-4 border-black`}
            >
              <p className="text-center text-[10px] text-sm sm:text-base font-semibold">
                I often do this, or have often done this within the past 3 years{" "}
                <br /> <b>(D)</b>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          {assessmentQuestionnaire.list.map((list, x) => {
            return (
              <>
                <tr className="border-y-4 border-black" key={x}>
                  <td
                    colSpan={smallScreen ? 4 : 5}
                    className="bg-gray-200 p-4 uppercase text-base sm:text-lg font-bold"
                  >
                    {list.subject}
                  </td>
                </tr>
                {list.description.map((desc, y) => {
                  return smallScreen ? (
                    <>
                      <tr className="border-y-2 border-black" key={x}>
                        <td
                          colSpan={4}
                          className="px-4 py-3 border-x-2 border-black text-sm sm:text-base"
                        >
                          {desc}
                        </td>
                      </tr>
                      <tr key={y} className="border-y-2 border-black">
                        <td className="px-4 py-3 border-x-2 border-black text-center">
                          <input
                            checked={
                              values[`subject_${x}_description_${y}`] === "A"
                            }
                            disabled={formSection === "submitted-form"}
                            id={`subject-${x}-description-${y}-radio-btn`}
                            type="radio"
                            onChange={() =>
                              setFieldValue(
                                `subject_${x}_description_${y}`,
                                "A"
                              )
                            }
                            value={values[`subject_${x}_description_${y}`]}
                            name={`subject_${x}_description_${y}`}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                          />
                        </td>
                        <td className="px-4 py-3 border-x-2 border-black text-center">
                          <input
                            checked={
                              values[`subject_${x}_description_${y}`] === "B"
                            }
                            disabled={formSection === "submitted-form"}
                            id={`subject-${x}-description-${y}-radio-btn`}
                            type="radio"
                            onChange={() =>
                              setFieldValue(
                                `subject_${x}_description_${y}`,
                                "B"
                              )
                            }
                            value={values[`subject_${x}_description_${y}`]}
                            name={`subject_${x}_description_${y}`}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                          />
                        </td>
                        <td className="px-4 py-3 border-x-2 border-black text-center">
                          <input
                            checked={
                              values[`subject_${x}_description_${y}`] === "C"
                            }
                            disabled={formSection === "submitted-form"}
                            id={`subject-${x}-description-${y}-radio-btn`}
                            type="radio"
                            onChange={() =>
                              setFieldValue(
                                `subject_${x}_description_${y}`,
                                "C"
                              )
                            }
                            value={values[`subject_${x}_description_${y}`]}
                            name={`subject_${x}_description_${y}`}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                          />
                        </td>
                        <td className="px-4 py-3 border-x-2 border-black text-center">
                          <input
                            checked={
                              values[`subject_${x}_description_${y}`] === "D"
                            }
                            disabled={formSection === "submitted-form"}
                            id={`subject-${x}-description-${y}-radio-btn`}
                            type="radio"
                            onChange={() =>
                              setFieldValue(
                                `subject_${x}_description_${y}`,
                                "D"
                              )
                            }
                            value={values[`subject_${x}_description_${y}`]}
                            name={`subject_${x}_description_${y}`}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                          />
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr key={y} className="border-y-2 border-black">
                      <td
                        className="px-4 py-3 border-x-2 border-black text-sm sm:text-base"
                        hidden={smallScreen}
                      >
                        {desc}
                      </td>
                      <td className="px-4 py-3 border-x-2 border-black text-center">
                        <input
                          checked={
                            values[`subject_${x}_description_${y}`] === "A"
                          }
                          disabled={formSection === "submitted-form"}
                          id={`subject-${x}-description-${y}-radio-btn`}
                          type="radio"
                          onChange={() =>
                            setFieldValue(`subject_${x}_description_${y}`, "A")
                          }
                          value={values[`subject_${x}_description_${y}`]}
                          name={`subject_${x}_description_${y}`}
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                        />
                      </td>
                      <td className="px-4 py-3 border-x-2 border-black text-center">
                        <input
                          checked={
                            values[`subject_${x}_description_${y}`] === "B"
                          }
                          disabled={formSection === "submitted-form"}
                          id={`subject-${x}-description-${y}-radio-btn`}
                          type="radio"
                          onChange={() =>
                            setFieldValue(`subject_${x}_description_${y}`, "B")
                          }
                          value={values[`subject_${x}_description_${y}`]}
                          name={`subject_${x}_description_${y}`}
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                        />
                      </td>
                      <td className="px-4 py-3 border-x-2 border-black text-center">
                        <input
                          checked={
                            values[`subject_${x}_description_${y}`] === "C"
                          }
                          disabled={formSection === "submitted-form"}
                          id={`subject-${x}-description-${y}-radio-btn`}
                          type="radio"
                          onChange={() =>
                            setFieldValue(`subject_${x}_description_${y}`, "C")
                          }
                          value={values[`subject_${x}_description_${y}`]}
                          name={`subject_${x}_description_${y}`}
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                        />
                      </td>
                      <td className="px-4 py-3 border-x-2 border-black text-center">
                        <input
                          checked={
                            values[`subject_${x}_description_${y}`] === "D"
                          }
                          disabled={formSection === "submitted-form"}
                          id={`subject-${x}-description-${y}-radio-btn`}
                          type="radio"
                          onChange={() =>
                            setFieldValue(`subject_${x}_description_${y}`, "D")
                          }
                          value={values[`subject_${x}_description_${y}`]}
                          name={`subject_${x}_description_${y}`}
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 peer-checked:bg-sky-600"
                        />
                      </td>
                    </tr>
                  );
                })}
              </>
            );
          })}
          {smallScreen && (
            <tr className="border-y-4 border-black">
              <td colSpan={smallScreen ? 4 : 5} className="bg-sky-400 p-4">
                <p className="uppercase text-base xl:text-lg text-white font-bold">
                  Accumulated Points
                </p>
              </td>
            </tr>
          )}
          <tr className="border-y-4 border-black">
            <td className="bg-sky-400 p-4 text-left" hidden={smallScreen}>
              <p className="uppercase text-base xl:text-lg text-white font-bold">
                Accumulated Points
              </p>
            </td>
            <td className="bg-sky-400 border-x-4 border-black">
              <p className="text-center text-base xl:text-lg text-white font-bold">
                {totalOptionA * 1}
              </p>
            </td>
            <td className="bg-sky-400 border-x-4 border-black">
              <p className="text-center text-base xl:text-lg text-white font-bold">
                {totalOptionB * 2}
              </p>
            </td>
            <td className="bg-sky-400 border-x-4 border-black">
              <p className="text-center text-base xl:text-lg text-white font-bold">
                {totalOptionC * 3}
              </p>
            </td>
            <td className="bg-sky-400 border-x-4 border-black">
              <p className="text-center text-base xl:text-lg text-white font-bold">
                {totalOptionD * 4}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
      <SignatureInput
        name="signature"
        label="Candidate Signature:"
        signatureRef={signatureRef}
        signatureDataURL={values.signature}
        handleSave={handleSave}
        handleClear={handleClear}
        error={errors?.signature && touched?.signature && errors.signature}
        descriptionText="Signature is required, please fill in your signature within the box provided below"
        hidden={formSection === "submitted-form"}
      />
      <button
        className="uppercase tracking-widest bg-black text-white w-full p-2 rounded-xl text-xl sm:text-2xl xl:text-3xl font-semibold disabled:opacity-30"
        onClick={handleSubmit}
        disabled={isSubmitting}
        hidden={formSection === "submitted-form"}
      >
        {isSubmitting && <Spinner />}
        Submit
      </button>
    </div>
  );
}

export default QuestionnaireForm;
